import { logClientError } from '@utils/server-log';
import { Injectable } from '@angular/core';
import {
  FEATURE_RESOURCE_MAP,
  PermissionAction,
  PermissionResource,
  USER_PERMISSIONS_STORAGE_KEY,
  buildPermission,
  parsePermission,
} from '@models/permissions';
import { BehaviorSubject } from 'rxjs';

/**
 * Stores and evaluates the logged-in user's permissions (`resource:action`).
 * Loaded via GetUserPermissions once after login / session refresh.
 */
@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private permissions: string[] | undefined = undefined;
  private readonly permissionsSubject = new BehaviorSubject<string[] | undefined>(undefined);

  readonly permissions$ = this.permissionsSubject.asObservable();

  /**
   * True after GetUserPermissions has been called in this SPA session.
   * Not restored from sessionStorage — forces a fresh fetch after page reload.
   */
  fetchedFromApi = false;

  constructor() {
    this.restoreFromSession();
  }

  get current(): string[] {
    return this.permissions ?? [];
  }

  get hasCachedPermissions(): boolean {
    return this.permissions !== undefined;
  }

  setPermissions(permissions: string[] | undefined | null): void {
    const normalized = permissions ?? [];
    this.permissions = normalized;
    this.fetchedFromApi = true;
    this.permissionsSubject.next(normalized);
    this.persistToSession(normalized);
  }

  clear(): void {
    this.permissions = undefined;
    this.fetchedFromApi = false;
    this.permissionsSubject.next(undefined);
    sessionStorage.removeItem(USER_PERMISSIONS_STORAGE_KEY);
  }

  hasPermission(permission: string): boolean {
    if (!permission) {
      return false;
    }
    return this.current.includes(permission);
  }

  hasAnyPermission(...permissions: string[]): boolean {
    return permissions.some((permission) => this.hasPermission(permission));
  }

  hasAllPermissions(...permissions: string[]): boolean {
    return permissions.every((permission) => this.hasPermission(permission));
  }

  can(resource: PermissionResource | string, action: PermissionAction | string): boolean {
    return this.hasPermission(buildPermission(resource, action));
  }

  canRead(resource: PermissionResource | string): boolean {
    return this.can(resource, 'read');
  }

  canWrite(resource: PermissionResource | string): boolean {
    return this.can(resource, 'write');
  }

  canUpdate(resource: PermissionResource | string): boolean {
    return this.can(resource, 'update');
  }

  canDelete(resource: PermissionResource | string): boolean {
    return this.can(resource, 'delete');
  }

  /**
   * Module visibility (menu / route): requires `<resource>:read`, sem exceção.
   * `roles` exige `roles:read` — não é mais herdado de `settings:update`.
   */
  canAccessFeature(featureKey: string): boolean {
    const mapped = FEATURE_RESOURCE_MAP[featureKey] ?? featureKey;
    return this.canRead(mapped);
  }

  /** Resources for which the user has `:read`. */
  getReadableResources(): string[] {
    const resources = new Set<string>();

    for (const permission of this.current) {
      const parsed = parsePermission(permission);
      if (parsed?.action === 'read') {
        resources.add(parsed.resource);
      }
    }

    return Array.from(resources);
  }

  /** True when the user has at least one `:read`. */
  hasAnyModuleAccess(): boolean {
    return this.getReadableResources().length > 0;
  }

  private restoreFromSession(): void {
    try {
      const raw = sessionStorage.getItem(USER_PERMISSIONS_STORAGE_KEY);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        this.permissions = parsed.filter((item): item is string => typeof item === 'string');
        this.permissionsSubject.next(this.permissions);
        // `fetchedFromApi` continua false de propósito: o cache serve só para
        // não piscar o menu enquanto o GetUserPermissions não responde.
      }
    } catch (error) {
      logClientError('[PermissionsService] Failed to restore permissions from sessionStorage:', error);
      sessionStorage.removeItem(USER_PERMISSIONS_STORAGE_KEY);
    }
  }

  private persistToSession(permissions: string[]): void {
    try {
      sessionStorage.setItem(USER_PERMISSIONS_STORAGE_KEY, JSON.stringify(permissions));
    } catch (error) {
      logClientError('[PermissionsService] Failed to persist permissions:', error);
    }
  }
}
